import {Alert, AlertDescription, AlertIcon, AlertTitle, Box, Code, Skeleton} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import React from 'react'
import {useContentSlot} from '../../hooks/useScapiCustomApi'
import PlainContent from '../plain-content'
import SimpleCmsContent from '../simple-cms-content'
import StyledContent from '../../leap/components/styled-content'
import CMSContent from '../cms-content'
import {TYPE_CONTENT_SLOT} from '../cms-content/hooks/use-cms-content'
import {isServer} from '../../utils/utils'

/**
 * Loads html content from content slot and render in place.
 */
const ContentSlot = ({
    slotId,
    context,
    contextId,
    showError = true,
    variant = 'simple',
    disableSkeleton,
    ...htmlProps
}) => {
    const {
        data: content,
        isLoading: loading,
        error
    } = useContentSlot({
        parameters: {context, contextId, slotId}
    })

    // server side fetch error will retrigger the fetch on client side, so we should handle it as loading
    if (loading || (isServer && error)) {
        return !disableSkeleton ? (
            <Skeleton height="7.5rem" {...htmlProps} data-testid="content-slot-loading" />
        ) : null
    }

    if (error) {
        if (showError) {
            return (
                <Alert {...htmlProps} status="error">
                    <AlertIcon />
                    <Box>
                        <AlertTitle>
                            <span>Something bad happened while loading&nbsp;</span>
                            <Code>{slotId}</Code>
                            <span>!</span>
                        </AlertTitle>
                        <AlertDescription>{error.toString()}</AlertDescription>
                    </Box>
                </Alert>
            )
        } else {
            console.error(`Error wile loading ${slotId}. ${error.toString()}`, error)
            return null
        }
    }

    if (!content?.c_body || content?.c_body?.replace(/\s+/g, '')?.trim() === '') {
        return null
    }

    switch (variant) {
        case 'plain':
            return <PlainContent {...htmlProps} content={content?.c_body} />
        case 'cms':
            // do not render CMSContent without content as it'll try to load the slot as cms content
            return content ? (
                <CMSContent
                    {...htmlProps}
                    id={slotId}
                    type={TYPE_CONTENT_SLOT}
                    content={content}
                    showError={showError}
                />
            ) : null
        case 'simple-cms':
            return <SimpleCmsContent {...htmlProps} content={content?.c_body} />
        default:
            return <StyledContent {...htmlProps} content={content?.c_body} />
    }
}

ContentSlot.propTypes = {
    /**
     * The slot id
     */
    slotId: PropTypes.string.isRequired,
    /**
     * The slot context could category, folder or global
     */
    context: PropTypes.string,
    /**
     * The slot context identifier could be a category or folder id
     */
    contextId: PropTypes.string,
    /**
     * Whether to show error message or not, when disabled the component will return null on error
     */
    showError: PropTypes.bool,
    /**
     * The variant to use for rendering the html content:
     * - plain - renders the content as is
     * - simple - renders the content with default styling and lazy loading of images
     * - cms - renders the content as is, lazy loading of images and supports widgets
     * - simple-cms - renders the content as is and lazy loading of images
     */
    variant: PropTypes.oneOf(['plain', 'simple', 'cms', 'simple-cms']),
    disableSkeleton: PropTypes.bool
}

export default ContentSlot
